import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, TooltipProps } from 'primereact/tooltip';
import { useFullscreenDialogParent } from '../general/use-fullscreen-dialog-parent';
import '../assets/long-text-tooltip.css';
import { MarkdownText } from './markdown-text';

/** Keep author-supplied text inside the viewport and scroll it independently. */
export const LongTextTooltip = forwardRef<Tooltip, Omit<TooltipProps, 'content'> & { content?: string }>(function LongTextTooltip({ className, appendTo, content, onBeforeShow, onShow, onHide, ...props }, ref) {
    const parent = useFullscreenDialogParent();
    const [text, setText] = useState(content ?? '');
    const tooltip = useRef<Tooltip | null>(null);
    const observer = useRef<ResizeObserver | null>(null);
    useEffect(() => () => observer.current?.disconnect(), []);
    const fitTooltip = useCallback(() => {
        const element = tooltip.current?.getElement();
        if (!element) return;
        const rect = element.getBoundingClientRect();
        element.style.position = 'fixed';
        element.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8))}px`;
        element.style.top = `${Math.max(8, Math.min(rect.top, window.innerHeight - rect.height - 8))}px`;
        element.style.right = '';
    }, []);
    const setTooltip = useCallback((instance: Tooltip | null) => {
        tooltip.current = instance;
        if (typeof ref === 'function') ref(instance);
        else if (ref) ref.current = instance;
    }, [ref]);
    return <Tooltip ref={setTooltip} position="right" event="both" autoHide={false} hideDelay={150}
        pt={{ root: { 'aria-hidden': false } }} {...props}
        onBeforeShow={event => {
            const source = event.target.getAttribute('data-markdown-tooltip') ?? content ?? '';
            if (!source.trim()) return false;
            setText(source);
            return onBeforeShow?.(event);
        }}
        onShow={event => {
            // PrimeReact's collision placement can use document coordinates after scrolling.
            // Clamp the final measured popup in viewport coordinates before it is painted.
            requestAnimationFrame(() => {
                const element = tooltip.current?.getElement();
                if (!element) return;
                fitTooltip();
                observer.current?.disconnect();
                if (typeof ResizeObserver !== 'undefined') {
                    observer.current = new ResizeObserver(fitTooltip);
                    observer.current.observe(element);
                }
            });
            onShow?.(event);
        }}
        onHide={event => { observer.current?.disconnect(); onHide?.(event); }}
        appendTo={appendTo ?? parent ?? document.body}
        className={`long-text-tooltip${className ? ` ${className}` : ''}`}>
        <MarkdownText text={content ?? text} />
    </Tooltip>;
});
